/**
 * Note TOC Inline Component
 *
 * Always-visible TOC for wide screens (≥1025px)
 * - Renders NoteTOCSectionList directly (no popover)
 * - Width controlled by parent flex layout
 * - Sticky positioning handled by parent
 */

import React, { useMemo } from 'react';
import type { NoteSection } from '@features/notes';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { useNoteTOC } from '../../hooks';
import { clearCopySectionSelection, toggleCopySectionSelection } from '../../store/chatSlice';
import { selectSelectedSectionIdsForCopy } from '../../store/selectors';

import { NoteTOCSectionList } from './NoteTOCSectionList';

export interface NoteTOCInlineProps {
  /** Array of note sections to display */
  sections: NoteSection[];
  /** CSS selector for the scrollable container containing sections */
  containerSelector?: string;
  /** Handler for copying selected sections */
  onCopySelected?: () => void;
  /** Note ID for analytics tracking */
  noteId?: string;
  /** Encounter ID for analytics tracking */
  encounterId?: string;
}

/**
 * Inline TOC component for wide screens
 *
 * Features:
 * - Always visible (no hover/click required)
 * - Width and positioning handled by parent layout
 */
export const NoteTOCInline: React.FC<NoteTOCInlineProps> = ({
  sections,
  containerSelector,
  onCopySelected,
  noteId,
  encounterId,
}) => {
  const dispatch = useAppDispatch();
  const { noteSection } = useEventTracking();
  const selectedSectionIds = useAppSelector(selectSelectedSectionIdsForCopy);

  // Convert to TOC format and get active tracking
  const tocSections = useMemo(
    () =>
      sections.map(s => ({
        section_id: s.section_id,
        name: s.name,
      })),
    [sections],
  );

  const { activeSectionId, scrollToSection } = useNoteTOC(tocSections, containerSelector);

  const handleCopySelected = () => {
    onCopySelected?.();
  };

  const handleDeselectAll = () => {
    noteSection.deselectAllClicked({
      source: 'toc',
      selected_count: selectedSectionIds.length,
      note_id: noteId,
      encounter_id: encounterId,
    });
    dispatch(clearCopySectionSelection());
  };

  if (sections.length === 0) {
    return null;
  }

  return (
    <NoteTOCSectionList
      sections={sections}
      activeSectionId={activeSectionId}
      selectedSectionIds={selectedSectionIds}
      onSectionClick={scrollToSection}
      onToggleSelection={sectionId => dispatch(toggleCopySectionSelection(sectionId))}
      onCopySelected={handleCopySelected}
      onDeselectAll={handleDeselectAll}
      noteId={noteId}
      encounterId={encounterId}
      noteSection={noteSection}
    />
  );
};
